import { type CalendarDate, parseDate } from '@internationalized/date';
import type { CalendarState } from '@react-stately/calendar';
import type { DateValue } from 'react-aria';

import type { DisablePresets } from './types';

/**
 * Check if a date is before the minimum allowed date
 */
export function isBeforeMin(date: CalendarDate, minValue: CalendarDate | DateValue | null): boolean {
  if (!minValue) {
    return false;
  }

  return date.compare(minValue as CalendarDate) < 0;
}

/**
 * Check if a date is after the maximum allowed date
 */
export function isAfterMax(date: CalendarDate, maxValue: CalendarDate | DateValue | null): boolean {
  if (!maxValue) {
    return false;
  }

  return date.compare(maxValue as CalendarDate) > 0;
}

/**
 * Check if a date is disabled based on future preset
 */
export function isDisabledByFuturePreset(date: CalendarDate, todayDate: CalendarDate): boolean {
  return date.compare(todayDate) > 0;
}

/**
 * Check if a date is disabled based on past preset
 */
export function isDisabledByPastPreset(date: CalendarDate, todayDate: CalendarDate): boolean {
  return date.compare(todayDate) < 0;
}

/**
 * Check if a date is a weekend (Saturday or Sunday)
 * Utility function available for future use or custom presets
 */
// oxlint-disable-next-line no-unused-vars -- Utility function for future use
export function isWeekend(date: CalendarDate): boolean {
  const jsDate = date.toDate('UTC');
  const dayOfWeek = jsDate.getUTCDay();

  return dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
}

/**
 * Check if a date is a specific day of the week
 * Utility function available for future use or custom presets
 * @param date - The date to check
 * @param dayOfWeek - Day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
 */
// oxlint-disable-next-line no-unused-vars -- Utility function for future use
export function isDayOfWeek(date: CalendarDate, dayOfWeek: number): boolean {
  const jsDate = date.toDate('UTC');
  const day = jsDate.getUTCDay();

  return day === dayOfWeek;
}

/**
 * Check if a date is disabled based on weekday presets
 */
export function isDisabledByWeekdayPresets(date: CalendarDate, presets: DisablePresets): boolean {
  const jsDate = date.toDate('UTC');
  const dayOfWeek = jsDate.getUTCDay();

  if (presets.weekends && (dayOfWeek === 0 || dayOfWeek === 6)) {
    return true;
  }

  if (presets.mondays && dayOfWeek === 1) {
    return true;
  }

  if (presets.tuesdays && dayOfWeek === 2) {
    return true;
  }

  if (presets.wednesdays && dayOfWeek === 3) {
    return true;
  }

  if (presets.thursdays && dayOfWeek === 4) {
    return true;
  }

  return Boolean(presets.fridays && dayOfWeek === 5);
}

/**
 * Check if a date is in the list of specific disabled dates
 */
export function isInDisabledDates(date: CalendarDate, dates: (CalendarDate | string)[]): boolean {
  return dates.some((disabledDate) => date.compare(toCalendarDate(disabledDate)) === 0);
}

/**
 * Convert a string or CalendarDate to CalendarDate
 * @param date - ISO date string (YYYY-MM-DD) or CalendarDate
 * @returns CalendarDate object
 */
function toCalendarDate(date: CalendarDate | string): CalendarDate {
  if (typeof date === 'string') {
    return parseDate(date);
  }

  return date;
}

/**
 * Check if a date is in any of the disabled ranges
 */
export function isInDisabledRanges(
  date: CalendarDate,
  ranges: { start: CalendarDate | string; end: CalendarDate | string }[],
): boolean {
  return ranges.some((range) => isInRange(date, range.start, range.end));
}

/**
 * Check if a date is within a disabled range
 */
function isInRange(date: CalendarDate, start: CalendarDate | string, end: CalendarDate | string): boolean {
  const startDate = toCalendarDate(start);
  const endDate = toCalendarDate(end);

  return date.compare(startDate) >= 0 && date.compare(endDate) <= 0;
}

/**
 * Check if a month is disabled based on future/past presets
 */
export function isMonthDisabledByTimePreset(
  month: number,
  currentYear: number,
  todayMonth: number,
  todayYear: number,
  presets: DisablePresets,
): boolean {
  // If we're in a different year, compare years
  if (currentYear !== todayYear) {
    if (presets.future && currentYear > todayYear) {
      return true;
    }

    return Boolean(presets.past && currentYear < todayYear);
  }

  // Same year, compare months
  if (presets.future && month > todayMonth) {
    return true;
  }

  return Boolean(presets.past && month < todayMonth);
}

/**
 * Check if a year is disabled based on future/past presets
 */
export function isYearDisabledByTimePreset(year: number, todayYear: number, presets: DisablePresets): boolean {
  if (presets.future && year > todayYear) {
    return true;
  }

  return Boolean(presets.past && year < todayYear);
}

/**
 * Check if a month range is fully disabled
 */
export function isMonthRangeFullyDisabled(
  month: number,
  currentYear: number,
  ranges: { start: CalendarDate | string; end: CalendarDate | string }[],
  state: CalendarState,
): boolean {
  const daysInMonth = new Date(currentYear, month, 0).getDate();
  const firstDay = state.focusedDate.set({ year: currentYear, month, day: 1 });
  const lastDay = state.focusedDate.set({ year: currentYear, month, day: daysInMonth });

  return ranges.some((range) => isRangeFullyDisabled(firstDay, lastDay, range.start, range.end));
}

/**
 * Check if a date range is fully within a disabled range
 */
function isRangeFullyDisabled(
  rangeStart: CalendarDate,
  rangeEnd: CalendarDate,
  disabledStart: CalendarDate | string,
  disabledEnd: CalendarDate | string,
): boolean {
  const startDate = toCalendarDate(disabledStart);
  const endDate = toCalendarDate(disabledEnd);

  return rangeStart.compare(startDate) >= 0 && rangeEnd.compare(endDate) <= 0;
}

/**
 * Check if a year range is fully disabled
 */
export function isYearRangeFullyDisabled(
  year: number,
  ranges: { start: CalendarDate | string; end: CalendarDate | string }[],
  state: CalendarState,
): boolean {
  const firstDay = state.focusedDate.set({ year, month: 1, day: 1 });
  const lastDay = state.focusedDate.set({ year, month: 12, day: 31 });

  return ranges.some((range) => isRangeFullyDisabled(firstDay, lastDay, range.start, range.end));
}
